import React from "react";
import {
    Alert,
    Box,
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { Add, Place } from "@mui/icons-material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { CustomerAddress } from "../../../types";
import { DeliveryArea } from "../../../services/deliveryAreaService";
import { DeliveryServiceSimple } from "../../../services/deliveryService";
import { formatCurrencyBDT } from "../../../utils/currency";
import { OrderFormData } from "./orderFormSchema";

const taka = (n: number) => formatCurrencyBDT(n, { minimumFractionDigits: 0 });

interface Props {
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    /** The customer's saved addresses (empty for a new customer). */
    addresses: CustomerAddress[];
    selectedAddressId: string;
    onPickAddress: (address: CustomerAddress | null) => void;
    /** Typing in the address fields un-picks a saved address. */
    onAddressTyped: () => void;
    areas: DeliveryArea[];
    /** Picking an area also fills the delivery charge. */
    onAreaChange: (areaId: string) => void;
    deliveryServices: DeliveryServiceSimple[];
    deliveryHint?: string;
}

/** Where it goes and how: saved addresses in one tap, area, courier, notes. */
export const DeliverySection: React.FC<Props> = ({
    control,
    errors,
    addresses,
    selectedAddressId,
    onPickAddress,
    onAddressTyped,
    areas,
    onAreaChange,
    deliveryServices,
    deliveryHint,
}) => (
    <Box sx={{ display: "grid", gap: 1.5 }}>
        {addresses.length > 0 && (
            <Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {addresses.map((address) => {
                        const selected = address.address_id === selectedAddressId;
                        return (
                            <Chip
                                key={address.address_id}
                                icon={<Place />}
                                label={`${address.street_address}, ${address.city}${address.is_default ? " · default" : ""}`}
                                color={selected ? "primary" : "default"}
                                variant={selected ? "filled" : "outlined"}
                                onClick={() => onPickAddress(address)}
                                sx={{ maxWidth: "100%", height: "auto", py: 0.5, "& .MuiChip-label": { whiteSpace: "normal" } }}
                            />
                        );
                    })}
                    <Chip
                        icon={<Add />}
                        label="New address"
                        variant={selectedAddressId ? "outlined" : "filled"}
                        onClick={() => onPickAddress(null)}
                    />
                </Box>
                <FormHelperText>A saved address picked here becomes the customer's default once the order is saved.</FormHelperText>
            </Box>
        )}

        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" } }}>
            <Controller
                name="shipping_address"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        value={field.value ?? ""}
                        required
                        fullWidth
                        size="small"
                        label="Delivery address"
                        placeholder="House, road, area"
                        multiline
                        minRows={1}
                        error={!!errors.shipping_address}
                        helperText={errors.shipping_address?.message}
                        onChange={(e) => {
                            onAddressTyped();
                            field.onChange(e);
                        }}
                    />
                )}
            />
            <Controller
                name="shipping_city"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        value={field.value ?? ""}
                        required
                        fullWidth
                        size="small"
                        label="City"
                        error={!!errors.shipping_city}
                        helperText={errors.shipping_city?.message}
                        onChange={(e) => {
                            onAddressTyped();
                            field.onChange(e);
                        }}
                    />
                )}
            />
        </Box>

        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <Controller
                name="delivery_area_id"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small">
                        <InputLabel>Delivery area</InputLabel>
                        <Select
                            value={areas.some((a) => a.delivery_area_id === field.value) ? field.value : ""}
                            label="Delivery area"
                            onChange={(e) => onAreaChange(String(e.target.value))}
                        >
                            <MenuItem value="">
                                <em>None — type the charge</em>
                            </MenuItem>
                            {areas.map((area) => (
                                <MenuItem key={area.delivery_area_id} value={area.delivery_area_id}>
                                    {area.name} — {taka(Number(area.delivery_charge))}
                                    {area.free_above_amount != null && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                            free over {taka(Number(area.free_above_amount))}
                                        </Typography>
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            />
            <Controller
                name="delivery_service_id"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small">
                        <InputLabel>Courier</InputLabel>
                        <Select
                            {...field}
                            value={deliveryServices.some((s) => s.delivery_service_id === field.value) ? field.value : ""}
                            label="Courier"
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {deliveryServices.map((service) => (
                                <MenuItem key={service.delivery_service_id} value={service.delivery_service_id}>
                                    {service.name} ({service.type})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            />
        </Box>

        {deliveryHint && (
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
                From chat: {deliveryHint}
            </Alert>
        )}

        <Controller
            name="notes"
            control={control}
            render={({ field }) => (
                <TextField
                    {...field}
                    value={field.value ?? ""}
                    fullWidth
                    size="small"
                    label="Order notes"
                    placeholder="Delivery instructions or anything the team should know"
                    multiline
                    minRows={2}
                    error={!!errors.notes}
                    helperText={errors.notes?.message}
                />
            )}
        />
    </Box>
);

export default DeliverySection;
